import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormbirdInjectorService } from 'formbird-services';
import { ComponentDefinition } from '@formbird/types';
import * as cloneDeep from 'lodash/cloneDeep';
import { ChangedDocumentService } from 'formbird-services';
import { AbstractComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import '@formbird/guh-md/dist/guh-md.umd.cjs'
import type {ChangeEvent, Editor, UploadedMedia} from '@formbird/guh-md'
import {MatTooltip} from '@angular/material/tooltip';
import type {FileProviderService} from '@formbird/services';
import {UtilDocumentId} from '@formbird/shared';
import {Observable} from 'rxjs';
import {HttpEvent, HttpEventType} from '@angular/common/http';
import type {ProgressBarMode} from '@angular/material/progress-bar/progress-bar';
import { sclHandlebars } from '../../../../formbird-sc-shared/src/lib/handlebars/sclHandlebars';
import {ConfigService} from '../../../../formbird-services/src/lib/services/config/config.service';

interface UploadedResponse {
  fileNo: string
  basePath: string
  fileType: string
}

interface UploadOptions {
  fileReferenceTemplateId: any;
  fileNo: string
  file: File
  documentId: string
  documentName?: string
  docFieldName?: string
}

interface MarkdownComponentDefinition extends ComponentDefinition {
  fileReferenceTemplateId: any;
  defaultValue: any;
  handlebarsHelpers: any;
  enabled: any;
  label:any;
  hideLabel: boolean;
  hideBorder: boolean;
  displayMode:any;
  linkTargetOutput: string;
  useFullLink: string;
  linkTargetHandlebars: string;
}

@Component({
  templateUrl: './markdown.component.html',
  styleUrls: ['./markdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom
})
export class MarkdownComponent extends AbstractComponent<MarkdownComponentDefinition> implements OnInit, AfterViewInit {

  @ViewChild('editorElement', { static: false }) editorElement: ElementRef<Editor>;
  private readonly blockedHtmlTags = new Set([
    'script',
    'iframe',
    'object',
    'embed',
    'link',
    'meta',
    'base',
    'form'
  ]);
  private readonly uriAttributes = new Set(['href', 'src', 'xlink:href', 'formaction', 'action', 'poster']);
  private fileProviderService: FileProviderService;
  progressBarMode: ProgressBarMode = 'indeterminate';
  uploading = false;
  progress = 0;

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected formbirdInjectorService: FormbirdInjectorService,
    private configService: ConfigService,
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdRef);
    this.fileProviderService = this.formbirdInjectorService.get('FileProviderService');
  }

  onFieldValueChanged(fieldValue) {
    if (fieldValue === this.fieldValue) {
      return;
    }

    if (fieldValue == null) {
      fieldValue = '';
    }

    this.fieldValue = cloneDeep(fieldValue);
    if (this.editorElement.nativeElement) {
      this.editorElement.nativeElement.value = this.sanitizeHTML(this.fieldValue);
    }

    this.detectChanges();
  }

  ngOnInit() {
    super.ngOnInit();
    const compThis =this;
    const loadedDocumentSub = this.broadcastService.on('document:loaded').subscribe(async (docs) => {
      if(compThis.document.systemHeader.templateId === docs.documentData.templateId){
        setTimeout(async () => {
            compThis.handleComponentDefChange();
        },0);
      }
    });
    this.subs.add(loadedDocumentSub);
    sclHandlebars.initHandlebars(this.componentDefinition.handlebarsHelpers);
  }

  ngAfterViewInit() {
    this.editorElement.nativeElement.value = this.sanitizeHTML(this.fieldValue ?? this.componentDefinition.defaultValue);
    this.editorElement.nativeElement.uploadMedia = this.uploadMedia.bind(this)
    // currentVersion is undefined when document is yet to be saved for the first time
    if (this.document.systemHeader.currentVersion !== undefined) {
      this.editorElement.nativeElement.tab = 'preview'
    }
    this.editorElement.nativeElement.markdownDefinition = this.componentDefinition;
    this.editorElement.nativeElement.sclHandlebars = sclHandlebars;
    this.setFieldActivation()
  }

  sanitizeHTML(html: unknown): string {
    if (html == null) {
      return '';
    }

    const input = String(html);
    const doc = new DOMParser().parseFromString(input, 'text/html');

    for (const tagName of this.blockedHtmlTags) {
      doc.body.querySelectorAll(tagName).forEach((el) => el.remove());
    }

    doc.body.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const attrName = attr.name.toLowerCase();
        const attrValue = attr.value.trim();
        if (attrName.startsWith('on') || attrName === 'srcdoc') {
          el.removeAttribute(attr.name);
          return;
        }

        if (this.uriAttributes.has(attrName) && !this.isSafeUri(attrValue)) {
          el.removeAttribute(attr.name);
        }
      });
    });

    return doc.body.innerHTML ?? '';
  }

  private isSafeUri(uri: string): boolean {
    if (!uri) {
      return true;
    }

    const normalized = uri.replace(/\s+/g, '').toLowerCase();
    if (normalized.startsWith('javascript:') || normalized.startsWith('vbscript:')) {
      return false;
    }

    if (normalized.startsWith('data:')) {
      return normalized.startsWith('data:image/');
    }

    return true;
  }

  protected setFieldActivation() {
    if (!this.editorElement?.nativeElement) { return }
    this.editorElement.nativeElement.readonly = this.disabled;
  }

  protected onComponentDefChanged(componentDefinition) {
    super.onComponentDefChanged(componentDefinition);
    this.handleComponentDefChange()
  }

  private handleComponentDefChange() {
    if (this.componentDefinition.label && this.componentDefinition.hideLabel) {
      this.editorElement.nativeElement.placeholder = this.componentDefinition.label;
    }
    if (this.componentDefinition.hideBorder) {
      this.editorElement.nativeElement.style.border = 'none'
    }
    if (!(typeof this.componentDefinition.displayMode === 'undefined' || this.componentDefinition.displayMode === null)) {
      this.fnProcessDisplayMode(this.componentDefinition.displayMode);
    }else{
      this.fnProcessDisplayMode('dynamic');
    }
    if (!(typeof this.componentDefinition.enabled === 'undefined' || this.componentDefinition.enabled === null)) {
      this.disabled = this.componentDefinition.enabled === false
      this.fnProcessDisplayEnabledField();
    }
    this.detectChanges();
  }
  fnProcessDisplayMode(displayMode) {
    switch (displayMode) {
      case 'edit':
        // console.log('displayMode edit');
        this.editorElement.nativeElement.tab = 'edit';
        this.showHideControlSwitch(false);
        break;
      case 'view':
        // console.log('displayMode view');
        this.editorElement.nativeElement.tab = 'preview';
        this.showHideControlSwitch(false);
        break;
      case 'dynamic':
        // console.log('displayMode dynamic');
        if (this.document.systemHeader.currentVersion !== undefined) {
          if (!this.fieldValue) {
            this.editorElement.nativeElement.tab = 'edit';
          } else {
            this.editorElement.nativeElement.tab = 'preview';
          }
        }else{
          this.editorElement.nativeElement.tab = 'edit';
        }
        this.showHideControlSwitch(false);
        break;
      case 'editOnly':
        // console.log('displayMode editOnly',this.editorElement);
        this.editorElement.nativeElement.tab = 'edit';
        this.showHideControlSwitch(true);
        break;
      case 'viewOnly':
        // console.log('displayMode viewOnly',this.editorElement);
        this.editorElement.nativeElement.tab = 'preview';
        this.showHideControlSwitch(true);
        break;
    }
  }

  showHideControlSwitch(hideModeControlSwitch) {
    try {
      let guhToolBarElem = null;
      let guhSectionElem = null;
      if (this.editorElement && this.editorElement?.nativeElement) {
        const guhEditorElem = this.editorElement.nativeElement;
        guhToolBarElem = guhEditorElem.shadowRoot.querySelector('guh-toolbar');
        guhSectionElem = guhEditorElem.shadowRoot.querySelector('guh-toolbar').shadowRoot.querySelector('section');
      }
      if (hideModeControlSwitch) {
        if (guhToolBarElem && guhSectionElem) {
          guhToolBarElem.style.minWidth = '100px;';
          guhSectionElem.style.display = 'none';
        }
      } else {
        if (guhToolBarElem && guhSectionElem) {
          guhToolBarElem.style.minWidth = 'unset';
          guhSectionElem.style.display = 'flex';
        }
      }
      if (this.componentDefinition.displayMode === 'viewOnly') {
        if (guhToolBarElem) {
          guhToolBarElem.style.borderBottom = 'unset';
        }
      } else {
        if (guhToolBarElem) {
          guhToolBarElem.style.borderBottom = '#efefef 1px solid';
        }
      }
    } catch (err) {
      console.error('showHideControlSwitch', err.message())
    }
  }

  async uploadMedia(files: File[]): Promise<UploadedMedia[]> {
    const upload = (file) => new Promise<UploadedResponse>((resolve, reject) => {
      this.upload(file, resolve, reject)
    })
    const ret = []
    this.uploading = true
    for (const file of files) {
      const uploaded = await upload(file)
      const rec = {
        url: `/api/getFile/${uploaded.fileNo}`,
        alt: uploaded.basePath,
        file:uploaded,
        fileNo:uploaded.fileNo,
        isImage : this.isImageFile(file),
        basePath:uploaded.basePath,
        fileType: uploaded.fileType,
        fileName : file.name,
        name : file.name,
        fileURL : `api/getFile/${uploaded.fileNo}`
      }
      ret.push(rec)
    }
    this.uploading = false
    return ret
  }

  isImageFile(file) {
    const name = file.name || file.fileName;
    if (name) {
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
      const exts = this.configService.clientConfig().imageViewer.supportedExtensions;
      return exts.indexOf(ext) > -1;
    }
    return false;
  }

  showToolTip(tooltip: MatTooltip, formFieldContainer: HTMLDivElement, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= formFieldContainer.scrollWidth);
  }

  toggleTooltip(tooltip: any) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }

  onChangeEvent($event: ChangeEvent) {
    this.valueChanged($event.detail.value)
  }

  upload(file: File, success: (r: UploadedResponse) => void, err: (e: unknown) => void) {
    this.progressBarMode = 'query'
    const fileNo = UtilDocumentId.generateId();

    const uploadData: UploadOptions = {
      fileReferenceTemplateId: undefined,
      fileNo: fileNo,
      file: file,
      documentId: this.document.documentId,
      documentName: this.document.systemHeader.summaryName,
      docFieldName: this.fieldName
    };
    if (!(typeof this.componentDefinition.fileReferenceTemplateId === 'undefined' ||
      this.componentDefinition.fileReferenceTemplateId === null)) {
      uploadData.fileReferenceTemplateId = this.componentDefinition.fileReferenceTemplateId;
    }


    const uploader: Observable<unknown> = this.fileProviderService.uploadFile(uploadData)
    uploader.subscribe((event: HttpEvent<UploadedResponse>) => {
      switch (event.type) {
        case HttpEventType.Sent:
          this.progressBarMode = 'indeterminate'
          this.cdr.detectChanges()
          break;
        case HttpEventType.Response:
          this.progressBarMode = 'query';
          success(event.body)
          break;
        case HttpEventType.UploadProgress:
          if (event.total) {
            this.progressBarMode = 'determinate'
            const progress = (event.loaded / event.total) * 100;
            if (progress !== this.progress) {
              this.progress = progress
              this.cdr.detectChanges()
            }
          } else {
            if (this.progressBarMode !== 'indeterminate') {
              this.progressBarMode = 'indeterminate'
              this.cdr.detectChanges()
            }
          }
          break;
      }
    }, (error) => {
      console.error(error)
      err(error)
    })
  }

  fnProcessDisplayEnabledField() {
    try {
      let guhEditButtonElem = null;
      if (this.editorElement && this.editorElement?.nativeElement) {
        const guhEditorElem = this.editorElement.nativeElement;
        guhEditButtonElem = guhEditorElem.shadowRoot.querySelector('guh-toolbar').shadowRoot.querySelector('section>button');
      }
      if (this.disabled) {
        if (this.fieldValue) {
          this.fnProcessDisplayMode('viewOnly');
        } else {
          this.fnProcessDisplayMode('view');
          if(guhEditButtonElem){
            guhEditButtonElem.disabled = true;
            guhEditButtonElem.style.cursor = 'not-allowed';
          }
        }
      } else {
        if(guhEditButtonElem){
          guhEditButtonElem.disabled = false;
          guhEditButtonElem.style.cursor = 'unset';
        }
      }
    } catch (err) {
      console.error('fnProcessDisplayEnabledField', err.message())
    }
  }
}
